import strings from 'l10n'
import { DropDownOptions } from 'types/common'

/**
 * Central currency catalog for the UI.
 *
 * Each item represents one supported currency and keeps the mapping between:
 * - `code`: canonical code used by factor configuration (`enabledCurrencies`).
 * - `companyCurrencyCode`: code stored/used by company forms and imports.
 * - `dropdownLabel`: localized label shown in currency dropdowns.
 * - `settingsLabel`: plain label shown in the "Factor Currencies" settings card.
 * - `iconName`: SVG icon path used in the settings list.
 */
export const factorCurrenciesCatalog = [
  {
    code: 'AUD',
    companyCurrencyCode: 'AUD',
    dropdownLabel: strings.AUSTRALIAN_DOLLAR,
    settingsLabel: 'Australian Dollar',
    iconName: 'currencies/australian_dollar',
  },
  {
    code: 'GBP',
    companyCurrencyCode: 'GBP',
    dropdownLabel: strings.BRITISH_POUND,
    settingsLabel: 'British Pound',
    iconName: 'currencies/british_pound',
  },
  {
    code: 'CAD',
    companyCurrencyCode: 'CAD',
    dropdownLabel: strings.CANADIAN_DOLLAR,
    settingsLabel: 'Canadian Dollar',
    iconName: 'currencies/canadian_dollar',
  },
  {
    code: 'CNY',
    companyCurrencyCode: 'CNY',
    dropdownLabel: strings.CHINESE_YUAN,
    settingsLabel: 'Chinese Yuan',
    iconName: 'currencies/chinese_yuan',
  },
  {
    code: 'EUR',
    companyCurrencyCode: 'EUR',
    dropdownLabel: strings.EURO,
    settingsLabel: 'Euro',
    iconName: 'currencies/euro',
  },
  {
    code: 'USD',
    companyCurrencyCode: 'USD',
    dropdownLabel: strings.US_DOLLAR,
    settingsLabel: 'US Dollar',
    iconName: 'currencies/dollar',
  },
] as const

export type FactorCurrencyCode = (typeof factorCurrenciesCatalog)[number]['code']

export const currencyDropDownOptions: DropDownOptions[] = factorCurrenciesCatalog.map((currency) => ({
  label: currency.dropdownLabel,
  value: currency.companyCurrencyCode,
}))

const factorCurrencyDropdownValueMap = Object.fromEntries(
  factorCurrenciesCatalog.map((currency) => [currency.code, currency.companyCurrencyCode])
) as Record<FactorCurrencyCode, string>

const currencyDropdownOptionValues = new Set(currencyDropDownOptions.map((option) => option.value))

export const isCurrencyDropDownOptions = (options: DropDownOptions[] = []) =>
  options.length > 0 && options.every((option) => currencyDropdownOptionValues.has(option.value))

// Company currency codes (e.g. "AU") are not always valid ISO 4217 codes. Intl.NumberFormat
// requires the ISO code (e.g. "AUD"), so map to it for display/formatting purposes.
const companyCurrencyCodeToIso = Object.fromEntries(
  factorCurrenciesCatalog.map((currency) => [currency.companyCurrencyCode, currency.code])
) as Record<string, string>

export const toIsoCurrencyCode = (companyCurrencyCode?: string): string | undefined =>
  companyCurrencyCode ? (companyCurrencyCodeToIso[companyCurrencyCode] ?? companyCurrencyCode) : undefined

export const ledgerCurrencyDisplay: Record<string, { abbr: string; symbol: string }> = {
  USD: { abbr: 'USD', symbol: '$' },
  AUD: { abbr: 'AUD', symbol: '$' },
  CAD: { abbr: 'CAD', symbol: '$' },
  CNY: { abbr: 'CNY', symbol: '¥' },
  GBP: { abbr: 'GBP', symbol: '£' },
  EUR: { abbr: 'EUR', symbol: '€' },
}

export const getEnabledCurrencyDropDownOptions = (
  enabledCurrencies: FactorCurrencyCode[] | undefined,
  options: DropDownOptions[] = currencyDropDownOptions,
  selectedValue?: string
) => {
  if (!isCurrencyDropDownOptions(options) || enabledCurrencies === undefined) {
    return options
  }

  const enabledValues = new Set(enabledCurrencies.map((currency) => factorCurrencyDropdownValueMap[currency]))
  const filteredOptions = options.filter((option) => enabledValues.has(option.value))

  if (!selectedValue || filteredOptions.some((option) => option.value === selectedValue)) {
    return filteredOptions
  }

  const selectedOption = options.find((option) => option.value === selectedValue)
  return selectedOption ? [selectedOption, ...filteredOptions] : filteredOptions
}
